import * as React from 'react';
import {
  Editor,
  EditorState,
  Modifier,
  DraftHandleValue,
  EditorProps,
  ContentBlock,
  SelectionState,
  ContentState,
} from 'draft-js';

import { BlockInfo, WordInfo } from './models';
import KeyboardUtils, { TranscriptEditorCommand } from './keyboard-utils';
import * as draftUtils from './draft-utils';

import 'draft-js/dist/Draft.css';

enum EntityType {
  SYNC_WORD = 'SYNC_WORD',
  SYNC_SEGMENT = 'SYNC_SEGMENT',
}

type Props = {
  editable: boolean;
  editorRef?: React.RefObject<Editor>;
  editorState: EditorState;
  isRecording: boolean;
  keyboardUtils: KeyboardUtils;
  onChange: (editorState: EditorState, callback: (editorState: EditorState) => void) => void;
  playerRef: React.RefObject<HTMLMediaElement>;
  disableHighlight: boolean;
  handleMarkSegment: (content: ContentState, selection: SelectionState) => void;
  markingSpeakers: boolean;
} & EditorProps;

interface State {
  deleteLastEntity?: string;
}

class DraftEditorWrapper extends React.Component<Props, State> {
  keyboardUtils: KeyboardUtils;
  playerRef: React.RefObject<HTMLMediaElement>;

  constructor(props: Props) {
    super(props);

    this.state = {};

    this.keyboardUtils = props.keyboardUtils;
    this.playerRef = props.playerRef;
  }

  getWordIndexAndOffset = (wordOffset: number, data: WordInfo[]): { index: number; offset: number } => {
    let currentOffset = 0;
    let offset = 0;
    let index = 0;
    data.some(({ word }: WordInfo, i: number) => {
      if (currentOffset + word.length >= wordOffset) {
        offset = wordOffset - currentOffset;
        index = i;
        return true;
      } else {
        currentOffset += word.length;
        return false;
      }
    });

    return { index, offset };
  };

  getNewBlockData = (
    content: ContentState,
    blockKey: string,
    startOffset: number,
    endOffset: number,
    insideRange?: boolean,
  ): WordInfo[] => {
    const block = content.getBlockForKey(blockKey);
    const entityKey = this.getBlockEntity(block);

    if (!entityKey) {
      return [];
    }

    const data = this.getWordData(content, entityKey);
    const startWord = this.getWordIndexAndOffset(startOffset, data);
    const endWord = this.getWordIndexAndOffset(endOffset, data);

    let newData;
    if (insideRange) {
      newData = data
        .map((wordInfo, i) => {
          if (i === startWord.index) {
            const word = data[startWord.index];
            return {
              ...word,
              word: word.word.slice(startWord.offset, endOffset),
            };
          } else if (i === endWord.index) {
            const word = data[endWord.index];
            return {
              ...word,
              word: word.word.slice(0, endWord.offset),
            };
          } else {
            return wordInfo;
          }
        })
        .filter((wordInfo, i) => i >= startWord.index && i <= endWord.index && wordInfo.word !== '');
    } else if (startWord.index === endWord.index) {
      newData = data.map((wordInfo, i) => {
        if (i === startWord.index) {
          const asArray = wordInfo.word.split('');
          asArray.splice(startWord.offset, endWord.offset - startWord.offset);
          const word = asArray.join('');
          return {
            ...wordInfo,
            word,
          };
        }
        return wordInfo;
      });
    } else {
      newData = data
        .map((wordInfo, i) => {
          if (i === startWord.index) {
            const word = data[startWord.index];
            return {
              ...word,
              word: word.word.slice(0, startWord.offset),
            };
          } else if (i === endWord.index) {
            const word = data[endWord.index];
            return {
              ...word,
              word: word.word.slice(endWord.offset),
            };
          } else {
            return wordInfo;
          }
        })
        .filter((wordInfo, i) => (i <= startWord.index || i >= endWord.index) && wordInfo.word !== '');
      /*             const endChar = text[startOffset - 1];
                  if (endChar != ' ' && endOffset != text.length && startOffset != 0) {
                      const start = newData[startWord.index];
                      const end = newData[startWord.index + 1];
                      const newWord = {
                          word: start.word + end.word,
                          startTime: start.startTime,
                          endTime: end.endTime,
                      }
                      newData.splice(startWord.index, 2, newWord);
                  } */
    }
    return newData;
  };

  removeRange = (editorState: EditorState, chars?: string): EditorState => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();

    const { anchorKey, anchorOffset, focusKey, focusOffset, isBackward } = selection.toObject();

    const startKey = isBackward ? focusKey : anchorKey;
    const endKey = isBackward ? anchorKey : focusKey;
    const startOffset = isBackward ? focusOffset : anchorOffset;
    const endOffset = isBackward ? anchorOffset : focusOffset;
    const startBlock = content.getBlockForKey(startKey);
    const startEntityKey = this.getBlockEntity(startBlock);

    let newContent: ContentState;
    let newData: WordInfo[];
    let newBlockData: BlockInfo | undefined;

    if (selection.isCollapsed()) {
      newData = this.getWordData(content, startEntityKey);
    } else {
      if (startKey === endKey) {
        newData = this.getNewBlockData(content, startKey, startOffset, endOffset);
      } else {
        const startText = startBlock.getText();
        const newStartData = this.getNewBlockData(content, startKey, startOffset, startText.length);
        const newEndData = this.getNewBlockData(content, endKey, 0, endOffset);
        newData = newStartData.concat(newEndData);
        if (newStartData.length > 0 && newEndData.length > 0) {
          if (startBlock.getText()[startOffset - 1] !== ' ') {
            const firstMiddleWord = newStartData[newStartData.length - 1];
            const secondMiddleWord = newEndData[0];
            const middleWord = {
              ...firstMiddleWord,
              endTime: secondMiddleWord.endTime,
              word: firstMiddleWord.word.trimRight() + secondMiddleWord.word.trimLeft(),
            };
            newData.splice(newStartData.length - 1, 2, middleWord);
          }
        }
        const blocks = content.getBlocksAsArray();
        const { blockData } = blocks.reduce(
          (
            acc: {
              blockData: BlockInfo;
              done: boolean;
            },
            currentBlock: ContentBlock,
          ) => {
            const key = currentBlock.getKey();

            if (key === startKey) {
              const { startTime, speakerId } = content.getBlockForKey(key).getData().toObject() as BlockInfo;
              return {
                blockData: {
                  ...acc.blockData,
                  startTime,
                  speakerId,
                },
                done: startKey === endKey,
              };
            } else if (key === endKey) {
              const { endTime } = content.getBlockForKey(key).getData().toObject() as BlockInfo;
              let sId = acc.blockData.speakerId;
              if (!acc.blockData.speakerId) {
                const { speakerId } = content.getBlockForKey(key).getData().toObject() as BlockInfo;
                sId = speakerId;
              }

              return {
                ...acc,
                blockData: {
                  ...acc.blockData,
                  endTime,
                  speakerId: sId,
                },
                done: true,
              };
            } else {
              return acc;
            }
          },
          {
            blockData: {
              startTime: 0,
              endTime: 0,
            },
            done: false,
          },
        );
        newBlockData = blockData;
      }
    }

    const allEntitiesSelection = selection.merge({
      anchorOffset: isBackward ? content.getBlockForKey(anchorKey).getText().length : 0,
      focusOffset: isBackward ? 0 : content.getBlockForKey(focusKey).getText().length,
    });

    newContent = Modifier.removeRange(content, allEntitiesSelection, 'forward');

    newContent = this.createEntityAtSelection(newContent, newData, newContent.getSelectionAfter());

    const selectionAfter = newContent.getSelectionAfter();
    let newSelection = selectionAfter.merge({
      anchorOffset: startOffset,
      focusOffset: startOffset,
    });

    if (chars) {
      newContent = this.addChars(newContent, newSelection, chars);
      newSelection = newContent.getSelectionAfter();
    }

    if (newBlockData) {
      newContent = draftUtils.mergeBlockDataForSelection(newContent, newSelection, { ...newBlockData });
    }

    let newEditorState = EditorState.push(editorState, newContent, 'remove-range');
    newEditorState = EditorState.acceptSelection(newEditorState, newSelection);
    newEditorState = EditorState.forceSelection(newEditorState, newEditorState.getSelection());

    return newEditorState;
  };

  capitalizeWord = (editorState: EditorState, capitalize: boolean): DraftHandleValue => {
    const content = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const { anchorOffset } = selection.toObject();
    const blockKey = selection.getStartKey();
    const block = content.getBlockForKey(blockKey);
    const entityKey = this.getBlockEntity(block);
    const data = this.getWordData(content, entityKey);

    const { index, offset } = this.getWordIndexAndOffset(anchorOffset, data);
    const { word } = data[index];
    const newWord = (capitalize ? word.charAt(0).toUpperCase() : word.charAt(0).toLowerCase()) + word.slice(1);
    const newData = this.editWord(data, index, 0, newWord, newWord.length);
    let newContentState = content.replaceEntityData(entityKey, { data: newData });

    const newSelection = selection.merge({
      anchorOffset: anchorOffset - offset,
      focusOffset: anchorOffset - offset + newWord.length,
      isBackward: false,
    });

    newContentState = Modifier.replaceText(newContentState, newSelection, newWord, undefined, entityKey);

    let newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
    newEditorState = EditorState.acceptSelection(newEditorState, selection);
    newEditorState = EditorState.forceSelection(newEditorState, newEditorState.getSelection());
    this.props.onChange(newEditorState);

    return 'handled';
  };

  addChars = (content: ContentState, selection: SelectionState, chars: string): ContentState => {
    const startOffset = selection.getStartOffset();
    const blockKey = selection.getStartKey();
    const block = content.getBlockForKey(blockKey);
    const entityKey = this.getBlockEntity(block);
    if (!entityKey) {
      // No entity found, return contentState with new entity at selection
      const { startTime, endTime } = draftUtils.findTimestamps(content, selection);

      const entityData: WordInfo[] = [
        {
          startTime,
          endTime,
          word: chars,
        },
      ];

      const newContentState = this.createEntityAtSelection(content, entityData, selection);
      return newContentState;
    }
    const data = this.getWordData(content, entityKey);
    const text = block.getText();

    let newData;
    if (startOffset === text.length) {
      if (text[text.length - 1] === ' ') {
        const newWord = {
          ...data[data.length - 1],
          word: chars,
        };
        newData = data.concat(newWord);
      } else {
        const { index, offset } = this.getWordIndexAndOffset(startOffset, data);
        newData = this.editWord(data, index, offset, chars);
      }
    } else if (chars === ' ') {
      const { index, offset } = this.getWordIndexAndOffset(startOffset, data);
      newData = this.splitWords(data, index, offset);
    } else {
      const { index, offset } = this.getWordIndexAndOffset(startOffset, data);
      newData = this.editWord(data, index, offset, chars);
    }

    const newContentState = content.replaceEntityData(entityKey, { data: newData });

    const newContent = Modifier.replaceText(newContentState, selection, chars, undefined, entityKey);

    return newContent;
  };

  getWordData = (contentState: ContentState, entityKey: string): WordInfo[] => {
    const entity = contentState.getEntity(entityKey);
    const { data } = entity.getData() as { data: WordInfo[] };
    return data;
  };

  splitWords = (data: WordInfo[], wordIndex: number, wordOffset: number): WordInfo[] => {
    const newData = data.map((wordInfo) => wordInfo);
    const { word, startTime, endTime } = newData[wordIndex];

    // Split the duration
    const duration = Math.abs(endTime - startTime);
    const commonTime = startTime + duration / 2;

    const firstWord = {
      word: word.slice(0, wordOffset) + ' ',
      startTime,
      endTime: commonTime,
    };

    const secondWord = {
      word: word.slice(wordOffset),
      startTime: commonTime,
      endTime,
    };

    newData.splice(wordIndex, 1, firstWord, secondWord);
    return newData;
  };

  editWord = (data: WordInfo[], wordIndex: number, wordOffset: number, chars: string, remove?: number): WordInfo[] => {
    const newData = data.map((wordInfo) => wordInfo);
    const { word, startTime, endTime } = newData[wordIndex];
    const asArray = word.split('');
    asArray.splice(wordOffset, remove || 0, chars);
    const newWord = asArray.join('');
    const newWordInfo = {
      word: newWord,
      startTime,
      endTime,
    };
    newData.splice(wordIndex, 1, newWordInfo);
    return newData;
  };

  handleBeforeInput = (chars: string, editorState: EditorState, _: number): DraftHandleValue => {
    const contentState = editorState.getCurrentContent();
    const blockKey = editorState.getSelection().getStartKey();
    const block = contentState.getBlockForKey(blockKey);
    const entityKey = this.getBlockEntity(block);
    let newEditorState;

    if (entityKey) {
      // Entity found in block, replacing
      newEditorState = this.removeRange(editorState, chars);
    } else {
      // No entity found in block, creating new with timestamps from last/next block

      const { startTime, endTime } = draftUtils.findTimestamps(contentState, editorState.getSelection());

      const entityData: WordInfo[] = [
        {
          startTime,
          endTime,
          word: chars,
        },
      ];

      const newContentState = this.createEntityAtSelection(
        editorState.getCurrentContent(),
        entityData,
        editorState.getSelection(),
      );

      newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
    }
    this.props.onChange(newEditorState);
    return 'handled';
  };

  logBlocks = (contentState: ContentState, limit?: number) => {
    contentState.getBlocksAsArray().forEach((block, i) => {
      if (!limit || i < limit) {
        console.debug(block.toObject());
        const entities = this.getBlockEntities(block);
        if (entities.length === 0) {
          console.debug('No entities in block: ', block.getKey());
        } else {
          entities.forEach((entity) => {
            console.debug(
              this.getWordData(contentState, entity)
                .map((wordinfo) => wordinfo.word)
                .join(' '),
            );
          });
        }
      }
    });
  };

  handleRemove = (editorState: EditorState, forward?: boolean): DraftHandleValue => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const { anchorOffset, isBackward, focusOffset } = selection.toObject();
    const blockKey = selection.getStartKey();
    const block = content.getBlockForKey(blockKey);
    const text = block.getText();

    if (anchorOffset === 0 && focusOffset === 0 && !forward) {
      if (block === content.getFirstBlock()) {
        console.debug('ignoring backspace @ first block with offset 0');
        return 'handled';
      }
      const otherBlock = content.getBlockBefore(blockKey);
      if (otherBlock) {
        const newSelection = selection.merge({
          anchorKey: isBackward ? selection.getAnchorKey() : otherBlock.getKey(),
          anchorOffset: isBackward ? selection.getAnchorOffset() : otherBlock.getText().length,
          focusKey: isBackward ? otherBlock.getKey() : selection.getFocusKey(),
          focusOffset: isBackward ? otherBlock.getText().length : selection.getFocusOffset(),
          hasFocus: true,
        });
        let newEditorState = EditorState.acceptSelection(editorState, newSelection);
        newEditorState = this.removeRange(newEditorState);
        this.props.onChange(newEditorState);
        return 'handled';
      }
    } else if (anchorOffset === text.length && focusOffset === text.length && forward) {
      const otherBlock = content.getBlockAfter(blockKey);
      if (otherBlock) {
        const newSelection = selection.merge({
          anchorKey: selection.getAnchorKey(),
          anchorOffset: selection.getAnchorOffset(),
          focusKey: otherBlock.getKey(),
          focusOffset: 0,
          hasFocus: true,
          isBackward: false,
        });
        let newEditorState = EditorState.acceptSelection(editorState, newSelection);
        newEditorState = this.removeRange(newEditorState);
        this.props.onChange(newEditorState);
      }
      return 'handled';
    }

    if (!selection.isCollapsed()) {
      const newEditorState = this.removeRange(editorState);
      this.props.onChange(newEditorState);
      return 'handled';
    } else {
      const newSelection = new SelectionState({
        anchorKey: selection.getAnchorKey(),
        anchorOffset: selection.getAnchorOffset(),
        focusKey: selection.getAnchorKey(),
        focusOffset: selection.getFocusOffset() + (forward ? 1 : -1),
        isBackward: !forward,
        hasFocus: true,
      });

      let newEditorState = EditorState.acceptSelection(editorState, newSelection);
      newEditorState = EditorState.forceSelection(newEditorState, newEditorState.getSelection());
      newEditorState = this.removeRange(newEditorState);
      this.props.onChange(newEditorState);
      return 'handled';
    }
  };

  replaceEntityData = (content: ContentState, data: WordInfo[], block: ContentBlock): ContentState => {
    let newContentState;
    const entities = this.getBlockEntities(block);
    if (entities.length === 0) {
      console.debug('no entities in block', block.getKey());
      newContentState = content;
    } else {
      const entity = entities[0];
      newContentState = content.replaceEntityData(entity, { data });
    }
    return newContentState;
  };

  createEntityAtSelection = (
    contentState: ContentState,
    data: WordInfo[],
    selection?: SelectionState,
  ): ContentState => {
    let newContentState = contentState.createEntity(EntityType.SYNC_SEGMENT, 'MUTABLE', { data });

    newContentState = Modifier.replaceText(
      newContentState,
      selection || newContentState.getSelectionAfter(),
      data.map((wordInfo) => wordInfo.word).join(''),
      undefined,
      newContentState.getLastCreatedEntityKey(),
    );

    return newContentState;
  };

  mergeWordData = (first: WordInfo[], second: WordInfo[]): WordInfo[] => {
    const backwards = first[0].startTime > second[0].startTime;
    return backwards ? second.concat(first) : first.concat(second);
  };

  getBlockEntity = (block: ContentBlock): string => {
    const entities: string[] = [];
    block.findEntityRanges(
      (character) => {
        if (character.getEntity() !== null) {
          entities.push(character.getEntity());
          return true;
        }
        return false;
      },
      (start, end) => {},
    );
    if (entities.length > 1) {
      console.debug('More than 1 entity in block: ', block.getKey());
    }
    return entities[0];
  };

  getBlockEntities = (block: ContentBlock): string[] => {
    const entities: string[] = [];
    block.findEntityRanges(
      (character) => {
        if (character.getEntity() !== null) {
          entities.push(character.getEntity());
          return true;
        }
        return false;
      },
      (start, end) => {},
    );

    if (entities.length > 1) {
      console.debug('> 1 entities in block: ', block.getKey());
      console.debug(entities);
    }

    return entities;
  };

  getBlockLastEntitySelection = (block: ContentBlock, selection: SelectionState): SelectionState => {
    const entities: SelectionState[] = [];
    block.findEntityRanges(
      (character) => true,
      (start, end) => {
        entities.push(
          selection.merge({
            anchorOffset: start,
            focusOffset: end,
          }),
        );
      },
    );
    return entities[entities.length - 1];
  };

  getFirstUnmarkedBlock = (contentState: ContentState, key: string): string => {
    const blockBefore = contentState.getBlockBefore(key);
    if (blockBefore) {
      const { speakerId } = blockBefore.getData().toObject();
      if (!speakerId) {
        return this.getFirstUnmarkedBlock(contentState, blockBefore.getKey());
      } else {
        return key;
      }
    } else {
      return key;
    }
  };

  markSegments = (editorState: EditorState, skipCurrentBlock?: boolean): DraftHandleValue => {
    const { anchorKey } = editorState.getSelection().toObject();
    const contentState = editorState.getCurrentContent();

    let endBlockKey;
    if (skipCurrentBlock) {
      // const blockBefore = contentState.getBlockBefore(anchorKey);
      const endBlock = contentState.getBlockBefore(anchorKey);
      endBlockKey = endBlock ? endBlock.getKey() : anchorKey;
    } else {
      endBlockKey = anchorKey;
    }

    const newSelection = editorState.getSelection().merge({
      anchorKey: this.getFirstUnmarkedBlock(contentState, anchorKey),
      focusKey: endBlockKey,
      isBackward: false,
    });

    const { content, selection } = draftUtils.mergeSegments(editorState, newSelection);
    this.props.handleMarkSegment(content, selection);
    return 'handled';
  };

  handleMarkSegments = (editorState: EditorState): DraftHandleValue => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      console.debug('selection must be collapsed when marking segments');
      return 'handled';
    }

    const { anchorKey, anchorOffset } = selection.toObject();
    const contentState = editorState.getCurrentContent();
    const text = contentState.getBlockForKey(anchorKey).getText();

    if (anchorOffset !== text.length) {
      const newEditorState = draftUtils.splitSegments(editorState);
      return this.markSegments(newEditorState, true);
    } else {
      return this.markSegments(editorState);
    }
  };

  handleReturn = (_: any, editorState: EditorState): DraftHandleValue => {
    if (this.props.markingSpeakers) {
      return this.handleMarkSegments(editorState);
    }

    let newEditorState;
    const selection = editorState.getSelection();
    if (selection.isCollapsed()) {
      newEditorState = draftUtils.splitSegments(editorState);
    } else {
      // Removing selection before splitting blocks
      newEditorState = this.removeRange(editorState);
      newEditorState = draftUtils.splitSegments(newEditorState);
    }
    this.props.onChange(newEditorState);
    return 'handled';
  };

  handlePastedText = (text: string, html: string | undefined, editorState: EditorState): DraftHandleValue => {
    // To-do: insert multiple words
    const newLineChar = text.includes('\n');
    const multiLine = newLineChar && !text.endsWith('\n');
    if (newLineChar) {
      if (!multiLine) {
        const newEditorState = this.removeRange(editorState, text.replace('\n', ''));
        this.props.onChange(newEditorState);
      } else {
        console.debug('multiline paste detected');
      }
    } else {
      const newEditorState = this.removeRange(editorState, text);
      this.props.onChange(newEditorState);
    }
    return 'handled';
  };

  handleKeyCommand = (command: TranscriptEditorCommand, editorState: EditorState): DraftHandleValue => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getStartKey());
    const entityKey = block.getEntityAt(selection.getStartOffset());
    // TO-DO undo
    switch (command) {
      case 'transcript-editor-jump': {
        if (this.props.isRecording) return 'handled';

        if (entityKey) {
          const syncWordEntity = content.getEntity(entityKey);
          const { data } = syncWordEntity.getData() as { data: WordInfo[] };
          const { anchorOffset } = selection.toObject();
          const { index } = this.getWordIndexAndOffset(anchorOffset, data);

          if (this.playerRef.current) {
            this.playerRef.current.currentTime = data[index].startTime;
          }
        }

        return 'handled';
      }
      case 'transcript-editor-move-back': // fallthrough
      case 'transcript-editor-move-forward': {
        const syncWordEntityChar = block
          .getCharacterList()
          .slice(selection.getStartOffset())
          .find((char) => {
            if (!char) return false;
            const charEntityKey = char.getEntity();
            if (!charEntityKey) return false;
            const entity = content.getEntity(charEntityKey);
            return entity.getType() === EntityType.SYNC_SEGMENT && charEntityKey !== entityKey;
          });
        if (!syncWordEntityChar) return 'handled';
        const syncWordEntityKey = syncWordEntityChar.getEntity();
        if (!syncWordEntityKey) return 'handled';
        const syncWordEntity = content.getEntity(syncWordEntityKey);
        const wordInfo: WordInfo = syncWordEntity.getData();
        if (this.playerRef.current) {
          this.playerRef.current.currentTime = wordInfo.startTime;
        }
        return 'handled';
      }
      case 'transcript-editor-capitalize-uppercase':
        return this.capitalizeWord(editorState, true);
      case 'transcript-editor-capitalize-lowercase':
        return this.capitalizeWord(editorState, false);
      case 'backspace':
        return this.handleRemove(editorState);
      case 'delete':
        return this.handleRemove(editorState, true);
      default:
        return 'not-handled';
    }
  };

  blockStyleFunction = (block: ContentBlock) => {
    return `segment ${this.props.disableHighlight ? 'disable-highlight' : ''}`;
  };

  handleDrop = (): DraftHandleValue => 'handled';

  render() {
    const { editable, editorRef, editorState, onChange } = this.props;

    return (
      <Editor
        ref={editorRef}
        editorState={editorState}
        onChange={onChange}
        handleBeforeInput={this.handleBeforeInput}
        keyBindingFn={this.keyboardUtils.draftKeyBindingFn}
        handleKeyCommand={this.handleKeyCommand}
        handleReturn={this.handleReturn}
        handleDrop={this.handleDrop}
        handlePastedText={this.handlePastedText}
        stripPastedStyles={true}
        readOnly={!editable}
        blockStyleFn={this.blockStyleFunction}
      />
    );
  }
}

// Forward ref for ability to focus the editor
export default React.forwardRef<Editor, Props>((props, ref) => (
  <DraftEditorWrapper {...props} editorRef={ref as any} />
));
